"use client";
import React, { useEffect, useState, useContext } from "react";
import { connectUserWallet, getWallet, modifiedTokenHash, checkIsPremium, minPremiumAmount } from "../../Utils/utils";
import { WalletContext } from "../../contexts/Wallet-context";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiConfig, createConfig } from 'wagmi';
import { mainnet } from 'viem/chains';
import { http } from 'viem';
import { useAccount, useConnect, useDisconnect, useContractRead, useBalance } from 'wagmi';
import { erc20Abi, formatUnits } from "viem";
import { readContract } from '@wagmi/core';
const SGT_TOKEN_ADDRESS = '0xa0e7626287bd02cbe3531c65148261bf0c0ed98b';

// Simple injected connector for wagmi 2.x
function injectedConnector() {
  return () => ({
    id: 'injected',
    name: 'Browser Wallet (MetaMask, Coinbase)',
    type: 'injected',
    async connect() {
      const provider = window.ethereum;
      if (!provider) throw new Error('No browser wallet found. Please install MetaMask or Coinbase Wallet.');
      
      const accounts = await provider.request({ 
        method: 'eth_requestAccounts' 
      });
      return { 
        accounts: accounts.map(x => x),
        chainId: Number(await provider.request({ method: 'eth_chainId' }))
      };
    },
    async disconnect() {
      // Injected wallets don't have a disconnect method
    },
    async getAccounts() {
      const provider = window.ethereum;
      if (!provider) return [];
      const accounts = await provider.request({ method: 'eth_accounts' });
      return accounts.map(x => x);
    },
    async getChainId() {
      const provider = window.ethereum;
      if (!provider) throw new Error('No provider');
      const chainId = await provider.request({ method: 'eth_chainId' });
      return Number(chainId);
    },
    async getProvider() {
      return window.ethereum;
    },
    async isAuthorized() {
      try {
        const accounts = await this.getAccounts();
        return accounts.length > 0;
      } catch {
        return false;
      }
    },
    onAccountsChanged(accounts) {
      if (accounts.length === 0) {
        this.onDisconnect?.();
      } else {
        this.onConnect?.({ accounts: accounts.map(x => x) });
      }
    },
    onChainChanged(chainId) {
      this.onConnect?.({ chainId: Number(chainId) });
    },
    onConnect() {},
    onDisconnect() {},
  });
}

// WalletConnect connector for wagmi 2.x

function walletConnectConnector() {
  return () => ({
    id: 'walletConnect',
    name: 'WalletConnect (Trust Wallet, Rainbow, etc.)',
    type: 'walletConnect',
    async connect() {
      const WalletConnectProvider = (await import('@walletconnect/ethereum-provider')).default;

      // detect mobile user agent
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);

      const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
        ? window.location.origin
        : 'https://revolutionlab-001-site24.anytempurl.com';

      const provider = await WalletConnectProvider.init({
        projectId: '8333166464ccc14283231b37282cea35',
        chains: [1], // Mainnet
        // show QR modal on desktop, disable it on mobile (we will surface pairing URI in UI)
        showQrModal: true,
        qrModalOptions: { themeMode: 'dark' },
        metadata: {
          name: 'ShillGuard',
          description: 'ShillGuard DApp',
          url: origin,
          icons: [origin + '/favicon.ico'],
        },
      });

      // make provider immediately available on the connector so UI polling can read __wc_lastUri
      this._provider = provider;
      
      // attach display_uri handler: save pairing URI but DO NOT auto-redirect
      const onDisplayUri = (uri) => {
        try {
          console.log('[WC debug] display_uri (saved, user must open)', uri);
          // persist latest pairing URI on provider instance so UI can read it and surface an explicit "Open in wallet" button
          provider.__wc_lastUri = uri;
          // mark that a fresh URI is available
          provider.__wc_lastUriTimestamp = Date.now();
        } catch (e) {
          console.warn('[WC debug] onDisplayUri error', e);
        }
      };

      // attach once
      try { provider.on?.('display_uri', onDisplayUri); } catch (e) { /* ignore */ }

      // increase timeout to accommodate slower mobile wallet UIs
      const TIMEOUT_MS = 120000; // 2 minutes

      try {
        await Promise.race([
          provider.connect(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('WalletConnect connect timeout')), TIMEOUT_MS))
        ]);
      } catch (err) {
        // cleanup on timeout/failure
        try { provider.removeListener?.('display_uri', onDisplayUri); } catch (_) {}
        throw err;
      }

      const accounts = provider.accounts || [];
      const chainId = provider.chainId || Number(await provider.request?.({ method: 'eth_chainId' })) || 1;

      // store provider for later use, do not reload here (reload handled elsewhere)
      this._provider = provider;

      // remove display_uri handler after connect to avoid re-opening QR/URI
      try { provider.removeListener?.('display_uri', onDisplayUri); } catch (e) { /* ignore */ }

      return {
        accounts,
        chainId: Number(chainId)
      };
    },
    async disconnect() {
      if (this._provider) {
        await this._provider.disconnect();
        this._provider = null;
      }
    },
    async getAccounts() {
      if (!this._provider) return [];
      return this._provider.accounts || [];
    },
    async getChainId() {
      if (!this._provider) throw new Error('No provider');
      return Number(this._provider.chainId);
    },
    async getProvider() {
      return this._provider;
    },
    async isAuthorized() {
      try {
        const accounts = await this.getAccounts();
        return accounts.length > 0;
      } catch {
        return false;
      }
    },
    onAccountsChanged(accounts) {
      if (accounts.length === 0) {
        this.onDisconnect?.();
      } else {
        this.onConnect?.({ accounts });
      }
    },
    onChainChanged(chainId) {
      this.onConnect?.({ chainId: Number(chainId) });
    },
    onConnect() {
      
    },
    onDisconnect() {},
  });
}

// Create wagmi config for wagmi 2.x
const config = createConfig({
  chains: [mainnet],
  connectors: [injectedConnector(), walletConnectConnector()],
  transports: {
    [mainnet.id]: http(`https://eth-mainnet.g.alchemy.com/v2/AHOsWI9ZVnMhM--r7aSqH`),
  },
});

// Create a client
const queryClient = new QueryClient();

export function WalletOptions() {
  const { connect, connectors } = useConnect();

  return connectors.map((connector) => (
    <WalletOption
      key={connector.id}
      connector={connector}
      onClick={() => connect({ connector })}
    />
  ));
}

function WalletOption({ connector, onClick }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      const provider = await connector.getProvider();
      setReady(!!provider);
    })();
  }, [connector]);

  return (
    <button disabled={!ready} onClick={onClick}>
      {connector.name}
    </button>
  );
}

export function Connection() {
  const { address } = useAccount();
  const { disconnect } = useDisconnect();

  return (
    <div>
      {address && <div>{address}</div>}
      <button onClick={() => disconnect()}>Disconnect</button>
    </div>
  );
}

const WalletComponent = () => {
  const [walletAmount, setWalletAmount] = useState(0);
  const [openAcount, setOpenAcount] = useState(false);
  const walletContext = useContext(WalletContext);
  const { wallet, setWallet } = walletContext;
  const [connectClick, setConnectClick] = useState(false);
  const [isLogout, setIsLogout] = useState(false);
  const [isPremium, setIspremium] = useState(checkIsPremium(wallet));

  const openConnect = (e) => {
    // if (e?.target?.closest && e.target.closest('.popup-dialog')) {
    //   setOpen(true);
    // } else {
    //   setOpen(!open);
    // }
  };

  const Balance = React.memo((props) => {
    // get live data realtime instead localstorage data
    let { address, isConnected } = useAccount();
    if(address === undefined && props.address) {
        address = props.address;
    }
    
    //const { data: nativeBalance } = useBalance({ address, watch: true });
    let { data: sgtBalance, isLoading: sgtLoading, error: sgtError } =
    useBalance({
        address,
        token: SGT_TOKEN_ADDRESS,
        unit: 'ether',  // fix BigInt problem
    });

    let wallet = {
        address: props.address,
        amount: sgtBalance ? sgtBalance?.formatted : 0
    };
    
    let balanceString = "";
    if(sgtBalance && sgtBalance?.formatted && sgtBalance?.symbol) {
      balanceString = `${Math.round(sgtBalance?.formatted)} ${sgtBalance?.symbol}`;
      //walletContext.setWallet(wallet);
    }

    if(isConnected && !isLogout && !localStorage.getItem('userWallet') && !sgtLoading) {
        // Save to localStorage
        localStorage.setItem('userWallet', JSON.stringify(wallet));
        setTimeout(() => {
          window.location.reload();
        }, 1500);
    }
    if(isLogout && localStorage.getItem('userWallet')) {
        localStorage.removeItem('userWallet');
    }

    // useEffect(() => {
    //   if (!isConnected || !address || localStorage.getItem('userWallet')) return
    //     localStorage.setItem('userWallet', JSON.stringify(wallet))
    // }, [])


    return (
    <div>
      {sgtBalance && <>
        <span className="amount">
        {sgtLoading
            ? 'Loading…'
            : sgtError
            ? 'Error'
            : sgtBalance?.formatted
            ? balanceString
            : '0 SGT'}
        </span>
      </>}
    </div>
    )
  })

  const displayUserInfo = () => {
    const w = getWallet();
    if (w?.amount !== null && w?.amount !== undefined) {
      setWalletAmount(w.amount);
      walletContext.setWallet(w);
    }
  };

  const logout = () => {
    localStorage.removeItem('userWallet');
    setWalletAmount(0);
    walletContext.setWallet(null);
    setIsLogout(true);
    setOpenAcount(false);
  };

  useEffect(() => {
    displayUserInfo();
    const premium = checkIsPremium();
    setIspremium(premium);
  }, []);

  // Child component inside WagmiConfig so hooks work
  function WalletUI() {
    const walletContext = useContext(WalletContext);
    let { isConnected, address } = useAccount();
    const { disconnect } = useDisconnect();
    const { connect, connectors } = useConnect();
    const isWalletConnected = JSON.parse(localStorage.getItem('userWallet'));
    let [isPremium, setIsPremium] = useState(false);
    const [amount, setAmount] = useState(0);
    
    const checkConnection = async () => {
      const connector = injectedConnector()();
      const connected = await connector.isAuthorized();
      if (connected) {
        const accounts = await connector.getAccounts();
        const chainId = await connector.getChainId();
        const tokenAddress = SGT_TOKEN_ADDRESS;
        const walletAddress = accounts[0];
        if(tokenAddress && walletAddress) {
          const decimals = await readContract(config, {
            address: tokenAddress,
            abi: erc20Abi,
            functionName: 'decimals',
          })

          const balance = await readContract(config, {
            address: tokenAddress,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [walletAddress],
          })

          const formatted = formatUnits(balance, decimals)
          console.log(formatted) 
          const obj = {
            address: walletAddress,
            amount: formatted
          }
          walletContext.setWallet(JSON.stringify(obj));
          localStorage.setItem('userWallet', JSON.stringify(obj));
          const premium = checkIsPremium(obj);
        }
      }
    }

    useEffect(() => {
      // only proceed after user requested connect and wagmi reports a connected address
      if (!connectClick) return;
      if (!isConnected || !address) return;

      (async () => {
        try {
          const result = await connectUserWallet(address);
          if (result && !result.errorMsg) {
            displayUserInfo();

            // perform a single reload if requested and not yet done
            try {
              const pending = sessionStorage.getItem('sgt_reload_pending');
              const done = sessionStorage.getItem('sgt_reload_done');
              if (pending === '1' && done !== '1') {
                sessionStorage.setItem('sgt_reload_done', '1');
                // small delay to allow UI update/flush, then reload once
                setTimeout(() => { try { window.location.reload(); } catch(e) { /* ignore */ } }, 700);
              }
            } catch (e) { /* ignore sessionStorage errors */ }
          }
        } catch (e) {
          console.error('connectUserWallet failed', e);
        }
      })();
    }, [isConnected, address, connectClick]);

    const handleDisconnect = () => {
      setConnectClick(false);
      disconnect();
      logout();
      setOpenAcount(false);
    };

    const openAccountInfo = () => {
        if(openAcount) {
          setOpenAcount(false);
        }
        else {
          setOpenAcount(true);
        }
    };

    // small helper: on mobile, poll provider for pairing URI and open wallet deep-link once
    function startAutoOpenWcUri(connector) {
      try {
        const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
        if (!isMobile || !connector) return;

        let cancelled = false;
        const MAX_MS = 60000; // wait up to 60s
        const INTERVAL_MS = 700;
        const start = Date.now();

        (async function poll() {
          if (cancelled) return;
          if (Date.now() - start > MAX_MS) return;
          try {
            const prov = await connector.getProvider();
            const uri = prov?.__wc_lastUri || prov?.wc?.peerMeta?.uri || prov?.uri || null;
            if (uri) {
              // prefer MetaMask universal link which opens MetaMask mobile
              const deep = `https://metamask.app.link/wc?uri=${encodeURIComponent(uri)}`;
              try { window.location.href = deep; } catch (e) { window.location.href = uri; }
              cancelled = true;
              return;
            }
          } catch (_) { /* ignore */ }
          setTimeout(poll, INTERVAL_MS);
        })();

        // return a cancel function (not used now, but safe)
        return () => { cancelled = true; };
      } catch (e) { /* ignore */ }
    }

    const handleConnect = (connector) => {
      setConnectClick(true);
      // request a single reload after successful connect (guarded and cleared later)
      try { sessionStorage.setItem('sgt_reload_pending', '1'); } catch(e) { /* ignore */ }

      if(!localStorage.getItem('userWallet')) {
        connect({ connector });
      }
      setIsLogout(false);
      setOpenAcount(false);
    };

    // insert small UI helper inside WalletUI render area (where connectors are shown)
    // add this component just after the connectors map inside the connect popup
    function WcOpenPanel({ connector }) {
      const [uri, setUri] = useState(null);

      // poll provider for pairing URI (short-lived polling)
      useEffect(() => {
        let mounted = true;
        let t = null;
        async function poll() {
          try {
            const prov = await connector.getProvider();
            const u = prov?.__wc_lastUri || prov?.wc?.peerMeta?.uri || prov?.uri || null;
            if (mounted && u) setUri(u);
            if (!u && mounted) {
              t = setTimeout(poll, 800);
            }
          } catch {
            if (mounted) t = setTimeout(poll, 800);
          }
        }
        poll();
        return () => { mounted = false; if (t) clearTimeout(t); };
      }, [connector]);

      if (!uri) return null;

      const openWalletOptions = () => {
        // Open the WalletConnect web fallback which shows all supported wallet apps
        const fallback = `https://walletconnect.com/wc?uri=${encodeURIComponent(uri)}`;
        // Use same-window navigation on mobile so the wallet chooser deep-links back correctly
        window.location.href = fallback;
      };

      const copyUri = async () => {
        try {
          await navigator.clipboard?.writeText(uri);
          alert('Pairing URI copied. Open your wallet app and paste it if needed.');
        } catch (e) {
          alert('Failed to copy URI. Please long-press the button and copy manually.');
        }
      };

      const openMetaMaskDeepLink = () => {
        const deep = `https://metamask.app.link/wc?uri=${encodeURIComponent(uri)}`;
        // try MetaMask first if user prefers it
        window.location.href = deep;
      };

      return (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 6 }}>Open your wallet (choose an option):</div>
          <button onClick={openWalletOptions} className="gray-bg gray-border rounded" style={{ marginRight: 8 }}>
            Open Wallet Options (all wallets)
          </button>
          <button onClick={openMetaMaskDeepLink} className="gray-bg gray-border rounded" style={{ marginRight: 8 }}>
            Open in MetaMask
          </button>
          <button onClick={copyUri} className="gray-bg gray-border rounded">
            Copy pairing link
          </button>
        </div>
      );
    }

    return (
      <div className="wallet-comp gray-bg gray-border rounded align-left" onClick={openAccountInfo}>
        
        {address || (isWalletConnected && isWalletConnected.address)? (
          <>
            <div className="connected" style={{"minHeight": "42px"}}>
              <span>
                <span>{modifiedTokenHash(address !== undefined ? address : isWalletConnected.address)}</span>
                <Balance address={address !== undefined ? address : isWalletConnected.address} isPremium={isPremium}/>
              </span>
              <span className={isPremium ? "user-icon premium" : "user-icon"} onClick={openAccountInfo}></span>
              {openAcount ? (
                <div className="popup-dialog gray-bg rounded">
                  <div className="wallet-lbl">Wallet: <span>{address !== undefined ? address : isWalletConnected.address}</span></div>
                  <div className="h-20"></div>
                  <div><Balance address={address !== undefined ? address : isWalletConnected.address} /></div>
                  <button onClick={handleDisconnect} className="gray-bg gray-border rounded">Logout</button>
                </div>
              ) : null}
            </div>
          </>
        ) : (
            <div className="connect-w">
                <span>Connect wallet</span>
                <span className="user-icon"></span>
                {openAcount ? (
                <div className="popup-dialog gray-bg rounded align-left">
                    {connectors.map((connector, index) => (
                    <div 
                        key={index}
                        onClick={() => handleConnect(connector)} 
                        className="gray-bg gray-border rounded connector-btn"
                        style={{ marginBottom: '10px', width: '100%' }}
                    >
                        Connect with {connector.name}
                    </div>
                    ))}
                    <WcOpenPanel connector={connectors.find(c => c.id === 'walletConnect')} />
                </div>
                ) : null}
            </div>
        )}
      </div>
    );
  }

  return (
    <WagmiConfig config={config}>
      <QueryClientProvider client={queryClient}>
        <WalletUI />
      </QueryClientProvider>
    </WagmiConfig>
  );
};

export default WalletComponent;
